import { create } from 'zustand';
import type {
  DownloadMediaState,
  FileState,
  ReplaceMediaState,
  SaveMediaState,
} from '~/singlePageApp/components/uploader/types';
import { isNil, isNotNil } from '~/utilities/type-guards';
import type { NoOpFn } from '~/utilities/util';
import type { ImportMediaState } from './ImportTask';
import type { BulkActionState } from './BulkActionTask';
import type { BulkDownloadState } from './BulkDownloadTask';
import type { BackgroundJobState } from './BackgroundJobTask';
import type { EmailSentState } from './EmailSentTask';
import type { LiveExportState } from './LiveExportTask';
import type { UrlImportState } from './UrlImportTask';

export const NOTIFICATION_STORAGE_KEY = 'WISTIA_notification-queue';

export type TaskType =
  | 'BACKGROUND_JOB'
  | 'BULK_ACTION'
  | 'BULK_DOWNLOAD'
  | 'EDITOR_DOWNLOAD_MEDIA'
  | 'EDITOR_REPLACE_MEDIA'
  | 'EDITOR_SAVE_MEDIA'
  | 'EMAIL_SENT'
  | 'IMPORT'
  | 'LIVE_EXPORT'
  | 'UPLOAD'
  | 'URL_IMPORT';

export const BINARY_PROGRESS_TASKS: TaskType[] = [
  'IMPORT',
  'BULK_ACTION',
  'BULK_DOWNLOAD',
  'BACKGROUND_JOB',
  'EMAIL_SENT',
  'EDITOR_DOWNLOAD_MEDIA',
  'EDITOR_SAVE_MEDIA',
  'URL_IMPORT',
];

export type TaskDataType =
  | BackgroundJobState
  | BulkActionState
  | BulkDownloadState
  | DownloadMediaState
  | EmailSentState
  | FileState
  | GenericRecord
  | ImportMediaState
  | LiveExportState
  | ReplaceMediaState
  | UrlImportState;

type BaseTask = {
  id: string;
  TTL_MS: number;
  onCancelTask?: NoOpFn;
  onRetryTask?: NoOpFn;
  isComplete: boolean;
  isFailed: boolean;
};

export type ReplaceTaskType = BaseTask & {
  type: 'EDITOR_REPLACE_MEDIA';
  data: ReplaceMediaState;
};

export type DownloadTaskType = BaseTask & {
  type: 'EDITOR_DOWNLOAD_MEDIA';
  data: DownloadMediaState;
};

export type SaveMediaTaskType = BaseTask & {
  type: 'EDITOR_SAVE_MEDIA';
  data: SaveMediaState;
};

export type Task =
  | DownloadTaskType
  | ReplaceTaskType
  | SaveMediaTaskType
  | {
      id: string;
      type: TaskType;
      data: TaskDataType;
      TTL_MS: number;
      onCancelTask?: NoOpFn;
      onRetryTask?: NoOpFn;
      isComplete: boolean;
      isFailed: boolean;
    };

export type TaskCreationData = Omit<Task, 'isComplete' | 'isFailed'>;

type StoredNotificationQueue = [string, Task][];

export type NotificationStoreActionsType = {
  addTaskToQueue: (task: TaskCreationData, openTray?: boolean) => void;
  updateTaskInQueue: (taskId: string, data: TaskDataType) => void;
  setTaskComplete: (taskId: string, data?: TaskDataType) => void;
  setTaskFailed: (taskId: string, data?: TaskDataType) => void;
  retryTask: (taskId: string) => void;
  removeTaskFromQueue: (taskId: string) => void;
  cancelAllActiveTasks: NoOpFn;
  setNotificationTrayIsOpen: (open?: boolean) => void;
};

export type NotificationStoreType = {
  activeTaskCount: number;
  notificationQueue: Map<string, Task>;
  notificationTrayIsOpen: boolean;
  showOverallFailedStatus: boolean;
  notificationStoreActions: NotificationStoreActionsType;
};

export const DEFAULT_CONTEXT_VALUE: Omit<NotificationStoreType, 'notificationStoreActions'> = {
  activeTaskCount: 0,
  notificationQueue: new Map<string, Task>(),
  notificationTrayIsOpen: false,
  showOverallFailedStatus: false,
};

export const useNotificationStore = create<NotificationStoreType>()((set) => {
  /* Private methods */

  const updatedActiveTaskCount = (task: Task, state: NotificationStoreType): number =>
    !task.isComplete && !task.isFailed && state.activeTaskCount > 0
      ? state.activeTaskCount - 1
      : state.activeTaskCount;

  const retrieveStoredQueue = (): NotificationStoreType['notificationQueue'] => {
    const storedQueueData = localStorage.getItem(NOTIFICATION_STORAGE_KEY);

    if (isNil(storedQueueData)) {
      return new Map<string, Task>();
    }

    const now = Date.now();
    const storedQueue = JSON.parse(storedQueueData) as StoredNotificationQueue;
    const storedTasksToRender = storedQueue.filter(([_, task]) => now < task.TTL_MS);

    return new Map<string, Task>(storedTasksToRender);
  };

  /* Public methods */

  const addTaskToQueue = (taskCreationData: TaskCreationData, openTray = false) => {
    const task = {
      ...taskCreationData,
      isComplete: false,
      isFailed: false,
    };

    set((state) => {
      const updatedQueue = new Map<string, Task>(state.notificationQueue).set(task.id, task);
      return {
        ...state,
        activeTaskCount: state.activeTaskCount + 1,
        notificationQueue: updatedQueue,
        notificationTrayIsOpen: openTray,
      };
    });
  };

  const updateTaskInQueue = (taskId: string, data: TaskDataType) => {
    set((state) => {
      const task = state.notificationQueue.get(taskId);

      if (isNil(task)) {
        return { ...state };
      }

      const updatedTask = {
        ...task,
        data,
      };
      const updatedQueue = new Map<string, Task>(state.notificationQueue).set(taskId, updatedTask);

      return {
        ...state,
        notificationQueue: updatedQueue,
      };
    });
  };

  const setTaskComplete = (taskId: string, data?: TaskDataType) => {
    set((state) => {
      const task = state.notificationQueue.get(taskId);

      if (isNil(task)) {
        return { ...state };
      }

      const updatedCount = updatedActiveTaskCount(task, state);

      const completedTask = {
        ...task,
        isComplete: true,
        TTL_MS: Date.now() + task.TTL_MS,
      };

      if (isNotNil(data)) {
        completedTask.data = data;
      }

      const updatedQueue = new Map<string, Task>(state.notificationQueue).set(
        taskId,
        completedTask,
      );

      return {
        ...state,
        activeTaskCount: updatedCount,
        notificationQueue: updatedQueue,
      };
    });
  };

  const setTaskFailed = (taskId: string, data?: TaskDataType) => {
    set((state) => {
      const task = state.notificationQueue.get(taskId);

      if (isNil(task)) {
        return { ...state };
      }

      const updatedCount = updatedActiveTaskCount(task, state);

      const failedTask = {
        ...task,
        isFailed: true,
        TTL_MS: Date.now() + task.TTL_MS,
      };

      if (isNotNil(data)) {
        failedTask.data = data;
      }

      const updatedQueue = new Map<string, Task>(state.notificationQueue).set(taskId, failedTask);

      return {
        ...state,
        activeTaskCount: updatedCount,
        notificationQueue: updatedQueue,
        showOverallFailedStatus: true,
      };
    });
  };

  const retryTask = (taskId: string) => {
    set((state) => {
      const task = state.notificationQueue.get(taskId);

      if (isNil(task) || !task.isFailed || isNil(task.onRetryTask)) {
        return { ...state };
      }

      task.onRetryTask();

      const retriedTask = {
        ...task,
        isComplete: false,
        isFailed: false,
      };

      const updatedQueue = new Map<string, Task>(state.notificationQueue).set(taskId, retriedTask);

      return {
        ...state,
        activeTaskCount: state.activeTaskCount + 1,
        notificationQueue: updatedQueue,
      };
    });
  };

  const removeTaskFromQueue = (taskId: string) => {
    set((state) => {
      const task = state.notificationQueue.get(taskId);

      if (isNil(task)) {
        return { ...state };
      }

      if (!task.isComplete && !task.isFailed) {
        task.onCancelTask?.();
      }

      const updatedQueue = new Map<string, Task>(state.notificationQueue);
      updatedQueue.delete(taskId);

      const updatedCount = updatedActiveTaskCount(task, state);

      return {
        ...state,
        activeTaskCount: updatedCount,
        notificationQueue: updatedQueue,
      };
    });
  };

  const cancelAllActiveTasks = () => {
    set((state) => {
      const nonActiveTasks = Array.from(state.notificationQueue).filter(([_, task]) => {
        const isNotActive = task.isComplete || task.isFailed;
        task.onCancelTask?.();
        return isNotActive;
      });

      const updatedQueue = new Map<string, Task>(nonActiveTasks);

      return {
        ...state,
        activeTaskCount: 0,
        notificationQueue: updatedQueue,
      };
    });
  };

  const setNotificationTrayIsOpen = (open?: boolean) => {
    set((state) => {
      if (isNil(open)) {
        return {
          ...state,
          notificationTrayIsOpen: !state.notificationTrayIsOpen,
          showOverallFailedStatus: state.notificationTrayIsOpen
            ? false
            : state.showOverallFailedStatus,
        };
      }
      return {
        ...state,
        notificationTrayIsOpen: open,
        showOverallFailedStatus:
          !open && state.notificationTrayIsOpen ? false : state.showOverallFailedStatus,
      };
    });
  };

  return {
    ...DEFAULT_CONTEXT_VALUE,
    notificationQueue: retrieveStoredQueue(),
    notificationStoreActions: {
      addTaskToQueue,
      updateTaskInQueue,
      setTaskComplete,
      setTaskFailed,
      retryTask,
      removeTaskFromQueue,
      cancelAllActiveTasks,
      setNotificationTrayIsOpen,
    },
  };
});

export const getNotificationStoreActions = (
  store: NotificationStoreType,
): NotificationStoreActionsType => store.notificationStoreActions;

export const NotificationStoreSelectors = {
  getActiveTaskCount: (store: NotificationStoreType): NotificationStoreType['activeTaskCount'] =>
    store.activeTaskCount,
  getNotificationQueue: (
    store: NotificationStoreType,
  ): NotificationStoreType['notificationQueue'] => store.notificationQueue,
  getNotificationTrayIsOpen: (
    store: NotificationStoreType,
  ): NotificationStoreType['notificationTrayIsOpen'] => store.notificationTrayIsOpen,
  getShowOverallFailedStatus: (
    store: NotificationStoreType,
  ): NotificationStoreType['showOverallFailedStatus'] => store.showOverallFailedStatus,
};
